export type ServerColumnNumberFilter = {
  min?: string
  max?: string
  equals?: string
}

export type ServerTableColumnFilters = {
  text?: Record<string, string>
  number?: Record<string, ServerColumnNumberFilter>
  boolean?: Record<string, "true" | "false">
  select?: Record<string, string[]>
  created?: { from?: string; to?: string }
  updated?: { from?: string; to?: string }
}

export type ColumnFilterStateInput = {
  text: Record<string, string>
  number: Record<string, ServerColumnNumberFilter>
  boolean: Record<string, "all" | "true" | "false">
  select: Record<string, string[]>
  created: { from?: string; to?: string }
  updated: { from?: string; to?: string }
}

/** Serialize active column filters for GET /collections/:slug/items?columnFilters=… */
export function buildServerColumnFilters(
  input: ColumnFilterStateInput
): ServerTableColumnFilters | undefined {
  const out: ServerTableColumnFilters = {}

  const text: Record<string, string> = {}
  for (const [fieldName, raw] of Object.entries(input.text || {})) {
    const term = String(raw ?? "").trim()
    if (term) text[fieldName] = term
  }
  if (Object.keys(text).length > 0) out.text = text

  const number: Record<string, ServerColumnNumberFilter> = {}
  for (const [fieldName, range] of Object.entries(input.number || {})) {
    const equals = range?.equals?.trim() ?? ""
    const min = range?.min?.trim() ?? ""
    const max = range?.max?.trim() ?? ""
    if (equals || min || max) {
      number[fieldName] = {
        ...(equals ? { equals } : {}),
        ...(min ? { min } : {}),
        ...(max ? { max } : {}),
      }
    }
  }
  if (Object.keys(number).length > 0) out.number = number

  const boolean: Record<string, "true" | "false"> = {}
  for (const [fieldName, mode] of Object.entries(input.boolean || {})) {
    if (mode === "true" || mode === "false") boolean[fieldName] = mode
  }
  if (Object.keys(boolean).length > 0) out.boolean = boolean

  const select: Record<string, string[]> = {}
  for (const [fieldName, values] of Object.entries(input.select || {})) {
    if (!Array.isArray(values) || values.length === 0) continue
    const cleaned = Array.from(
      new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))
    )
    if (cleaned.length > 0) select[fieldName] = cleaned
  }
  if (Object.keys(select).length > 0) out.select = select

  const createdFrom = input.created?.from?.trim() ?? ""
  const createdTo = input.created?.to?.trim() ?? ""
  if (createdFrom || createdTo) {
    out.created = {
      ...(createdFrom ? { from: createdFrom } : {}),
      ...(createdTo ? { to: createdTo } : {}),
    }
  }

  const updatedFrom = input.updated?.from?.trim() ?? ""
  const updatedTo = input.updated?.to?.trim() ?? ""
  if (updatedFrom || updatedTo) {
    out.updated = {
      ...(updatedFrom ? { from: updatedFrom } : {}),
      ...(updatedTo ? { to: updatedTo } : {}),
    }
  }

  return Object.keys(out).length > 0 ? out : undefined
}

export function hasServerColumnFilters(
  filters: ServerTableColumnFilters | undefined | null
): boolean {
  return Boolean(filters && Object.keys(filters).length > 0)
}
